<div>
  <div class="container-fluid">
    <div class="mb-3">
      <a routerLink="/dji-flights" class="btn btn-outline-secondary">
        <i class="bi bi-arrow-left me-2"></i>Back to DJI Flights
      </a>
    </div>

    @if (loading) {
      <div class="text-center py-5">
        <div class="spinner-border text-primary" role="status"><span class="visually-hidden">Loading...</span></div>
      </div>
    } @else if (loadError && !flight) {
      <div class="alert alert-danger" role="alert">
        <i class="bi bi-exclamation-triangle-fill me-2"></i>{{ loadError }}
      </div>
    } @else if (flight) {
      <div class="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-2">
        <div>
          <h1 class="h2 mb-1 fw-bold">DJI flight</h1>
          <p class="text-muted mb-0">{{ formatInstant(flight.flightStartedAt) }}</p>
        </div>
        <span class="badge fs-6" [ngClass]="statusBadgeClass(flight.linkStatus)">{{ statusLabel(flight.linkStatus) }}</span>
      </div>

      @if (flight.reason) {
        <div class="alert alert-warning" role="alert">
          <div class="fw-semibold"><i class="bi bi-exclamation-triangle me-2"></i>Why this flight needs attention</div>
          {{ flight.reason.message }}
        </div>
      }
      @if (result) {
        <div class="alert alert-dismissible" [ngClass]="result.type === 'success' ? 'alert-success' : 'alert-info'" role="alert">
          {{ result.text }}
          <button type="button" class="btn-close" aria-label="Close" (click)="result = null"></button>
        </div>
      }
      @if (actionError) {
        <div class="alert alert-danger alert-dismissible" role="alert">
          <i class="bi bi-exclamation-triangle-fill me-2"></i>{{ actionError }}
          <div class="small">The flight has not been changed.</div>
          <button type="button" class="btn-close" aria-label="Close" (click)="actionError = ''"></button>
        </div>
      }

      <div class="row g-4">
        <div class="col-lg-8">
          <!-- Flight facts -->
          <div class="card mb-4">
            <div class="card-header fw-semibold">Flight</div>
            <div class="card-body">
              <dl class="row mb-0">
                <dt class="col-sm-4">Started</dt>
                <dd class="col-sm-8">{{ formatInstant(flight.flightStartedAt) }}</dd>
                <dt class="col-sm-4">Ended</dt>
                <dd class="col-sm-8">{{ formatInstant(flight.flightEndedAt) }}</dd>
                <dt class="col-sm-4">Duration</dt>
                <dd class="col-sm-8">{{ formatDuration(flight.durationSeconds) || '—' }}</dd>
                <dt class="col-sm-4">Area</dt>
                <dd class="col-sm-8">{{ flight.workAreaAcres != null ? (flight.workAreaAcres | number: '1.0-2') + ' acres' : '—' }}</dd>
                <dt class="col-sm-4">Applied</dt>
                <dd class="col-sm-8">
                  <span class="text-capitalize">{{ flight.usageType || '—' }}</span>
                  @if (formatApplied(flight); as applied) {
                    · {{ applied }}
                  }
                </dd>
                <dt class="col-sm-4">Average speed</dt>
                <dd class="col-sm-8">{{ formatSpeed(flight.avgSpeedMps) || 'Pending (comes with the detail file)' }}</dd>
                <dt class="col-sm-4">Detail file</dt>
                <dd class="col-sm-8">
                  {{ detailStatusLabel(flight.detailStatus) }}
                  @if (flight.detailFetchedAt) {
                    <span class="text-muted">· {{ formatInstant(flight.detailFetchedAt) }}</span>
                  }
                </dd>
                <dt class="col-sm-4">Received</dt>
                <dd class="col-sm-8">{{ formatInstant(flight.receivedAt) }}</dd>
              </dl>
            </div>
          </div>

          <!-- Links -->
          <div class="card mb-4">
            <div class="card-header fw-semibold">Links</div>
            @if (flight.links.length === 0) {
              <div class="card-body text-muted">Not linked to anything yet.</div>
            } @else {
              <ul class="list-group list-group-flush">
                @for (link of flight.links; track link.id) {
                  <li class="list-group-item d-flex justify-content-between align-items-center">
                    <div>
                      <div class="small text-muted">{{ linkTypeLabel(link.targetType) }}</div>
                      @if (linkTarget(link); as target) {
                        <a [routerLink]="target.commands" [queryParams]="target.queryParams">{{ link.label || link.targetId }}</a>
                      } @else {
                        <span>{{ link.label || link.targetId }}</span>
                      }
                    </div>
                    <span class="badge" [ngClass]="link.linkedBy ? 'bg-primary' : 'bg-secondary'">{{ link.linkedBy ? 'Set by an admin' : 'Automatic' }}</span>
                  </li>
                }
              </ul>
            }
          </div>

          <!-- Choose the drone and organization -->
          @if (resolving) {
            <div class="card mb-4 border-primary">
              <div class="card-header fw-semibold">Choose the drone and organization</div>
              <div class="card-body">
                @if (flight.droneCandidates.length > 0) {
                  <div class="mb-3">
                    <div class="form-label small text-body-secondary">Drones with this serial number</div>
                    <div class="list-group">
                      @for (candidate of flight.droneCandidates; track candidate.id) {
                        <button
                          type="button"
                          class="list-group-item list-group-item-action"
                          [class.active]="selectedDrone?.id === candidate.id"
                          (click)="pickCandidate(candidate)"
                        >
                          <div class="fw-semibold">{{ candidate.serialNumber }} · {{ candidate.name }}</div>
                          <div class="small">
                            {{ candidate.source === 'adn' ? 'ADN inventory' : 'Owned by ' + candidate.organizationName }}
                            ·
                            @if (candidate.ownerAtFlight.organizationName) {
                              held by {{ candidate.ownerAtFlight.organizationName }} at the time
                            } @else if (candidate.ownerAtFlight.kind === 'ambiguous') {
                              changed organization during the flight
                            } @else {
                              not assigned to anyone at the time
                            }
                          </div>
                        </button>
                      }
                    </div>
                  </div>
                }

                <div class="mb-3 position-relative">
                  <label class="form-label small text-body-secondary" for="drone-search">Or find any drone</label>
                  <input
                    id="drone-search"
                    type="search"
                    class="form-control"
                    placeholder="Serial number or name"
                    autocomplete="off"
                    [(ngModel)]="droneSearch"
                    (ngModelChange)="onDroneSearchInput($event)"
                  />
                  @if (droneResults.length > 0) {
                    <div class="list-group position-absolute w-100 shadow z-3">
                      @for (drone of droneResults; track drone.id) {
                        <button type="button" class="list-group-item list-group-item-action" (click)="pickSearched(drone)">
                          <div class="fw-semibold">{{ drone.serialNumber }}</div>
                          <div class="small text-muted">{{ drone.name }} · {{ drone.source === 'adn' ? 'ADN inventory' : drone.organizationName }}</div>
                        </button>
                      }
                    </div>
                  } @else if (searchingDrones) {
                    <div class="form-text">Searching…</div>
                  }
                </div>

                @if (selectedDrone) {
                  <div class="alert alert-light border mb-3">
                    <i class="bi bi-check-circle me-1"></i>Selected: <strong>{{ selectedDrone.serialNumber }}</strong> · {{ selectedDrone.name }}
                  </div>
                }

                <div class="mb-3">
                  <label class="form-label small text-body-secondary" for="resolve-org">Organization</label>
                  <select id="resolve-org" class="form-select" [(ngModel)]="selectedOrganizationId">
                    <option value="">Choose an organization</option>
                    @for (org of organizations; track org.id) {
                      <option [value]="org.id">{{ org.name }}</option>
                    }
                  </select>
                  <div class="form-text">It can be any organization, including one that does not match the drone's owner.</div>
                </div>

                <div class="d-flex gap-2">
                  <button type="button" class="btn btn-primary" [disabled]="!canSubmitResolve" (click)="confirm('setDrone')">Link drone and organization</button>
                  <button type="button" class="btn btn-outline-secondary" (click)="cancelResolving()">Cancel</button>
                </div>
              </div>
            </div>
          }

          <!-- History -->
          <div class="card mb-4">
            <div class="card-header fw-semibold">History</div>
            @if (flight.history.length === 0) {
              <div class="card-body text-muted">Nobody has acted on this flight yet.</div>
            } @else {
              <ul class="list-group list-group-flush">
                @for (entry of flight.history; track entry.id) {
                  <li class="list-group-item">
                    <div class="d-flex justify-content-between">
                      <span class="fw-semibold">{{ actionLabel(entry.action) }}</span>
                      <span class="small text-muted">{{ formatInstant(entry.createdAt) }}</span>
                    </div>
                    <div class="small text-muted">
                      by {{ entry.actorName || 'a removed account' }}
                      @if (entry.fromStatus && entry.toStatus && entry.fromStatus !== entry.toStatus) {
                        · {{ statusLabel(entry.fromStatus) }} → {{ statusLabel(entry.toStatus) }}
                      }
                    </div>
                    @if (detailNote(entry.details); as note) {
                      <div class="small fst-italic">“{{ note }}”</div>
                    }
                  </li>
                }
              </ul>
            }
          </div>
        </div>

        <div class="col-lg-4">
          <!-- Actions -->
          <div class="card mb-4">
            <div class="card-header fw-semibold">Actions</div>
            <div class="card-body d-grid gap-2">
              @if (actions?.setDrone) {
                <button type="button" class="btn btn-primary" [disabled]="resolving" (click)="startResolving()">
                  <i class="bi bi-link-45deg me-1"></i>{{ flight.drone ? 'Change drone and organization' : 'Choose drone and organization' }}
                </button>
              }
              @if (actions?.relink) {
                <button type="button" class="btn btn-outline-primary" (click)="confirm('relink')"><i class="bi bi-arrow-repeat me-1"></i>Re-run linkage</button>
              }
              @if (actions?.unlinkDrone) {
                <button type="button" class="btn btn-outline-secondary" (click)="confirm('unlinkDrone')">Unlink drone</button>
              }
              @if (actions?.unlinkSprayRecord) {
                <button type="button" class="btn btn-outline-secondary" (click)="confirm('unlinkRecord')">Unlink spray record</button>
              }
              @if (actions?.restore) {
                <button type="button" class="btn btn-success" (click)="confirm('restore')"><i class="bi bi-arrow-counterclockwise me-1"></i>Restore</button>
              }
              @if (actions?.dismiss) {
                <button type="button" class="btn btn-outline-warning" (click)="confirm('dismiss')">Dismiss</button>
              }
              @if (actions?.markInvalid) {
                <button type="button" class="btn btn-outline-danger" (click)="confirm('invalid')">Mark invalid</button>
              }
              @if (flight.sprayRecordId) {
                <div class="form-text">This flight is linked to a spray record. Unlink it before changing the drone, dismissing or marking the flight invalid.</div>
              }
              @if (isSetAside) {
                <div class="form-text">A dismissed or invalid flight can only be restored.</div>
              }
            </div>
          </div>

          <!-- Organization and device -->
          <div class="card mb-4">
            <div class="card-header fw-semibold">Organization</div>
            <div class="card-body">
              @if (flight.organizationId) {
                <a [routerLink]="['/organizations', flight.organizationId]">{{ flight.organizationName }}</a>
              } @else {
                <span class="badge bg-warning text-dark">No organization</span>
              }
            </div>
          </div>

          <div class="card mb-4">
            <div class="card-header fw-semibold">Device</div>
            <div class="card-body">
              <dl class="mb-0">
                <dt class="small text-muted">Serial number</dt>
                <dd class="text-break">{{ flight.device.factorySn || '—' }}</dd>
                <dt class="small text-muted">Hardware id</dt>
                <dd class="text-break">{{ flight.device.hardwareId || '—' }}</dd>
                <dt class="small text-muted">Body code</dt>
                <dd class="text-break mb-0">{{ flight.device.bodyCode || '—' }}</dd>
              </dl>
            </div>
          </div>
        </div>
      </div>

      <!-- Confirmations -->
      @switch (confirming) {
        @case ('setDrone') {
          <app-confirmation-modal
            title="Link this drone and organization?"
            [message]="setDroneMessage"
            confirmLabel="Link"
            variant="primary"
            [processing]="processing"
            processingLabel="Linking..."
            (confirmed)="onSetDroneConfirmed()"
            (cancelled)="cancelConfirm()"
          ></app-confirmation-modal>
        }
        @case ('relink') {
          <app-confirmation-modal
            title="Re-run linkage?"
            message="Automatic matching runs again for this flight, for example after a pilot has logged it. It never changes a drone you chose yourself."
            confirmLabel="Re-run"
            variant="primary"
            [processing]="processing"
            processingLabel="Running..."
            (confirmed)="onRelinkConfirmed()"
            (cancelled)="cancelConfirm()"
          ></app-confirmation-modal>
        }
        @case ('unlinkDrone') {
          <app-confirmation-modal
            title="Unlink the drone?"
            message="If you chose this drone and organization, both are cleared and automatic matching takes over again on the next run."
            confirmLabel="Unlink"
            variant="warning"
            [processing]="processing"
            processingLabel="Unlinking..."
            (confirmed)="onUnlinkConfirmed('drone')"
            (cancelled)="cancelConfirm()"
          ></app-confirmation-modal>
        }
        @case ('unlinkRecord') {
          <app-confirmation-modal
            title="Unlink the spray record?"
            message="The flight is unlinked from its spray record, and from the job linked through it. The record itself is not changed."
            confirmLabel="Unlink"
            variant="warning"
            [processing]="processing"
            processingLabel="Unlinking..."
            (confirmed)="onUnlinkConfirmed('spray_record')"
            (cancelled)="cancelConfirm()"
          ></app-confirmation-modal>
        }
        @case ('restore') {
          <app-confirmation-modal
            title="Restore this flight?"
            message="It goes back to being matched automatically, and its status is worked out again from what it is linked to."
            confirmLabel="Restore"
            variant="primary"
            [processing]="processing"
            processingLabel="Restoring..."
            (confirmed)="onRestoreConfirmed()"
            (cancelled)="cancelConfirm()"
          ></app-confirmation-modal>
        }
        @case ('dismiss') {
          <app-confirmation-modal-with-reason
            title="Dismiss this flight?"
            message="Use this for a flight that is not a real job, such as a test or practice flight. It stays out of automatic matching until restored."
            confirmLabel="Dismiss"
            variant="warning"
            reasonLabel="Note (optional)"
            reasonPlaceholder="e.g. Test flight at the shop"
            [reasonRequired]="false"
            [processing]="processing"
            (confirmed)="onDismissConfirmed($event)"
            (cancelled)="cancelConfirm()"
          ></app-confirmation-modal-with-reason>
        }
        @case ('invalid') {
          <app-confirmation-modal-with-reason
            title="Mark this flight invalid?"
            message="Use this for bad or duplicate data. It stays out of automatic matching until restored."
            confirmLabel="Mark invalid"
            variant="danger"
            reasonLabel="Note (optional)"
            reasonPlaceholder="e.g. Duplicate of another flight"
            [reasonRequired]="false"
            [processing]="processing"
            (confirmed)="onInvalidConfirmed($event)"
            (cancelled)="cancelConfirm()"
          ></app-confirmation-modal-with-reason>
        }
      }
    }
  </div>
</div>
